/**
 * useMeterList
 *
 * Lists the meters the signed-in user can see WITHIN THE ACTIVE TENANT and maps
 * meter selection onto the global `selectedSheet`. Selecting a meter therefore
 * switches the whole app (KPIs, charts, billing, header) to that meter's data —
 * which is correct and reuses every existing single-meter calculation untouched.
 *
 * A tenant can now have several meters (e.g. Taparia 2F + 3F), each its own
 * SheetSource sharing one access code. They are grouped here by `tenantId`.
 *
 * NOTE: an "All meters" combined total (summed energy/cost across meters) is a
 * separate, billing-sensitive feature — see MeterPicker's `showAll` (off until
 * that aggregation exists). Selection is per-meter only for now.
 */

import { useMemo } from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { MeterDescriptor, TelemetryRow } from '@/types';

interface UseMeterListResult {
  meters: MeterDescriptor[];
  selectedMeter: string | 'all';
  setSelectedMeter: (id: string | 'all') => void;
  /** Rows filtered to the selected meter (no time-window applied). */
  meterRows: TelemetryRow[];
}

export function useMeterList(rows: TelemetryRow[]): UseMeterListResult {
  const { user, selectedSheet, setSelectedSheet } = useAuth();

  // All meters this user can see in the CURRENTLY ACTIVE tenant. Falls back to
  // just the selected sheet (e.g. no user/access yet) so the UI never blanks.
  const groupSheets = useMemo(() => {
    if (!selectedSheet) return [];
    const all = user?.access.sheets ?? [selectedSheet];
    const group = all.filter(s => s.tenantId === selectedSheet.tenantId);
    return group.length ? group : [selectedSheet];
  }, [user, selectedSheet]);

  const meters: MeterDescriptor[] = useMemo(
    () => groupSheets.map(s => ({
      deviceId:    s.id,
      meterSerial: s.meta?.consumerId ?? s.sheetId,
      location:    s.meta?.location ?? '',
      label:       s.label,
    })),
    [groupSheets],
  );

  // Selection is the active sheet; switching a meter switches the whole app.
  const selectedMeter = selectedSheet?.id ?? 'all';
  const setSelectedMeter = (id: string | 'all') => {
    if (id === 'all') return; // combined view not implemented yet (see file header)
    const next = groupSheets.find(s => s.id === id);
    if (next && next.id !== selectedSheet?.id) setSelectedSheet(next);
  };

  // Rows already belong to the selected meter (useSheetData fetches selectedSheet).
  const meterRows: TelemetryRow[] = useMemo(() => rows, [rows]);

  return { meters, selectedMeter, setSelectedMeter, meterRows };
}
